/**
 * HRDashboard.jsx — Sprint 2 Iter241
 * ===================================
 * Dashboard RH unifié (4 onglets) — source /api/hr/dashboard
 *
 * Onglets :
 *   1. Coaching humain — quota, consumed, reserved, remaining
 *   2. COACHDIA Prism — invited / activated / completed
 *   3. COACHDIA Pulse — statut, éligibilité, rapport
 *   4. Vue globale — santé commande
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Briefcase, Sparkles, Gauge, Loader2, RefreshCw, CheckCircle2, Clock,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card } from '../../components/ui/card';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function HRDashboard() {
  const [data, setData] = useState(null);
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/hr/dashboard`, { headers: authHeaders() });
      setData(r.data);
      // charger la liste collabs du 1er scope
      const q = selectedOrder ? `?order_id=${encodeURIComponent(selectedOrder)}` : '';
      const c = await axios.get(`${API}/hr/dashboard/collaborators${q}`, { headers: authHeaders() });
      setCollabs(c.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement impossible');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedOrder]);

  if (loading && !data) return <div className="min-h-screen flex items-center justify-center bg-stone-50">
    <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
  </div>;

  if (!data || data.total_orders === 0) {
    return (
      <div className="min-h-screen bg-stone-50 p-8" data-testid="hr-dashboard-empty">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl p-8 text-center">
          <div className="font-display text-xl mb-2">Aucune commande dans votre périmètre</div>
          <p className="text-sm text-stone-500">Votre accès sera actif dès qu'une commande vous sera rattachée.</p>
        </div>
      </div>
    );
  }

  const { coaching, insights_individual, insights_collective, per_order } = data;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="hr-dashboard">
      <div className="bg-[#0F0F10] text-white py-5">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A]">Espace RH</div>
            <h1 className="font-display text-2xl">Pilotage global</h1>
            <p className="text-xs text-stone-400 mt-1">
              {data.total_orders} commande{data.total_orders > 1 ? 's' : ''} dans votre périmètre
            </p>
          </div>
          <button onClick={load} data-testid="hr-dashboard-refresh"
                  className="flex items-center gap-1.5 text-xs text-stone-300 hover:text-white">
            <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs defaultValue="global" className="w-full">
          <TabsList className="bg-white border border-stone-200 p-1 mb-5">
            <TabsTrigger value="global" data-testid="hr-tab-global">
              <Gauge className="w-3.5 h-3.5 mr-1.5" /> Vue globale
            </TabsTrigger>
            <TabsTrigger value="coaching" data-testid="hr-tab-coaching">
              <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Coaching humain
            </TabsTrigger>
            <TabsTrigger value="indiv" data-testid="hr-tab-indiv">
              <Users className="w-3.5 h-3.5 mr-1.5" /> COACHDIA Prism
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="hr-tab-team">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> COACHDIA Pulse
            </TabsTrigger>
          </TabsList>

          {/* 🌐 Vue globale */}
          <TabsContent value="global" className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <MetricCard icon={<Briefcase className="w-4 h-4" />}
                          label="Coaching" value={`${coaching.consumed_sessions}/${coaching.total_sessions}`}
                          subline={`${coaching.reserved_sessions} à venir · ${coaching.remaining_sessions} dispos`}
                          testid="hr-global-coaching" />
              <MetricCard icon={<Users className="w-4 h-4" />}
                          label="COACHDIA Prism"
                          value={`${insights_individual.tests_completed}/${insights_individual.total}`}
                          subline={`${insights_individual.invited} invités · ${insights_individual.growth_paths_generated} parcours`}
                          testid="hr-global-indiv" />
              <MetricCard icon={<Sparkles className="w-4 h-4" />}
                          label="COACHDIA Pulse"
                          value={`${insights_collective.reports_generated}/${insights_collective.orders_with_collective}`}
                          subline={`${insights_collective.reports_pending} en attente`}
                          testid="hr-global-team" />
            </div>

            <Card className="p-5" data-testid="hr-per-order-table">
              <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-3">Par commande</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-stone-500 border-b">
                      <th className="py-2">Entreprise / Commande</th>
                      <th>Statut</th>
                      <th>Coaching</th>
                      <th>COACHDIA Prism indiv</th>
                      <th>COACHDIA Pulse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {per_order.map(o => (
                      <tr key={o.order_id} className="border-b border-stone-50"
                          data-testid={`hr-order-row-${o.order_id}`}>
                        <td className="py-2.5">
                          <div className="font-medium text-stone-800">{o.company_name || '—'}</div>
                          <div className="text-[10px] text-stone-400">{o.order_id}</div>
                        </td>
                        <td><span className="px-2 py-0.5 rounded-full text-[10px] bg-stone-100">{o.status}</span></td>
                        <td>{o.coaching.consumed}/{o.coaching.total}
                          {o.coaching.reserved > 0 && <span className="text-[10px] text-stone-400"> ({o.coaching.reserved} à venir)</span>}
                        </td>
                        <td>{o.insights_individual.included
                              ? `${o.insights_individual.tests_completed}/${o.insights_individual.total}`
                              : <span className="text-stone-300">—</span>}
                        </td>
                        <td>{o.insights_collective.included
                              ? (o.insights_collective.status === 'generated'
                                  ? <span className="text-emerald-600">✓ Généré</span>
                                  : <span className="text-amber-600">En attente</span>)
                              : <span className="text-stone-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* 💼 Coaching */}
          <TabsContent value="coaching">
            <div className="grid md:grid-cols-4 gap-3 mb-4">
              <MetricCard label="Total sessions" value={coaching.total_sessions} testid="hr-coach-total" />
              <MetricCard label="Consommées" value={coaching.consumed_sessions} accent="#047857" testid="hr-coach-consumed" />
              <MetricCard label="À venir (réservées)" value={coaching.reserved_sessions} accent="#1E40AF" testid="hr-coach-reserved" />
              <MetricCard label="Disponibles" value={coaching.remaining_sessions} accent="#92710A" testid="hr-coach-remaining" />
            </div>
            <CollabsTable items={collabs.filter(c => (c.sessions_allocated || 0) > 0)}
                          columns={['sessions']} testid="hr-coaching-table" />
          </TabsContent>

          {/* 🧠 COACHDIA Prism */}
          <TabsContent value="indiv">
            <div className="grid md:grid-cols-5 gap-3 mb-4">
              <MetricCard label="Total" value={insights_individual.total} testid="hr-ind-total" />
              <MetricCard label="Invités" value={insights_individual.invited} testid="hr-ind-invited" />
              <MetricCard label="Activés" value={insights_individual.account_activated} accent="#1E40AF" testid="hr-ind-activated" />
              <MetricCard label="Tests terminés" value={insights_individual.tests_completed} accent="#047857" testid="hr-ind-completed" />
              <MetricCard label="Parcours générés" value={insights_individual.growth_paths_generated} accent="#92710A" testid="hr-ind-gp" />
            </div>
            <CollabsTable items={collabs.filter(c => c.insights_individual_included)}
                          columns={['insights']} testid="hr-indiv-table" />
          </TabsContent>

          {/* 👥 COACHDIA Pulse */}
          <TabsContent value="team">
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <MetricCard label="Commandes avec Team"
                          value={insights_collective.orders_with_collective} testid="hr-team-with" />
              <MetricCard label="Rapports générés"
                          value={insights_collective.reports_generated} accent="#047857" testid="hr-team-generated" />
              <MetricCard label="En attente"
                          value={insights_collective.reports_pending} accent="#92710A" testid="hr-team-pending" />
            </div>
            <Card className="p-5" data-testid="hr-team-per-order">
              <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-3">Par commande</div>
              {per_order.filter(o => o.insights_collective.included).map(o => (
                <div key={o.order_id} className="flex items-center justify-between border-b border-stone-50 py-2.5">
                  <div>
                    <div className="font-medium text-sm">{o.company_name}</div>
                    <div className="text-[10px] text-stone-400">{o.order_id}</div>
                  </div>
                  <div className="text-xs">
                    {o.insights_collective.status === 'generated' ? (
                      <a href={`/admin/team-insights/${o.insights_collective.report_id}`}
                         className="text-emerald-600 underline flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Consulter le rapport
                      </a>
                    ) : (
                      <span className="text-amber-600 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> En attente de génération
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {per_order.filter(o => o.insights_collective.included).length === 0 && (
                <div className="text-sm text-stone-500">Aucune commande avec COACHDIA Pulse dans votre périmètre.</div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, subline, accent = '#111827', testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="font-display text-2xl mt-1" style={{ color: accent }}>{value}</div>
      {subline && <div className="text-[11px] text-stone-400 mt-0.5">{subline}</div>}
    </Card>
  );
}

function CollabsTable({ items, columns, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-stone-500 border-b">
              <th className="py-2">Collaborateur</th>
              {columns.includes('sessions') && <>
                <th>Alloué</th><th>Utilisé</th><th>Réservé</th>
              </>}
              {columns.includes('insights') && <>
                <th>Invité</th><th>Activé</th><th>Test</th><th>Parcours</th>
              </>}
            </tr>
          </thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id} className="border-b border-stone-50" data-testid={`hr-collab-row-${c.id}`}>
                <td className="py-2">
                  <div className="font-medium">{(c.first_name || '') + ' ' + (c.last_name || '')}</div>
                  <div className="text-[10px] text-stone-400">{c.email}</div>
                </td>
                {columns.includes('sessions') && <>
                  <td>{c.sessions_allocated || 0}</td>
                  <td>{c.sessions_used || 0}</td>
                  <td>{c.sessions_reserved || 0}</td>
                </>}
                {columns.includes('insights') && <>
                  <td>{c.insights_invitation_sent_at ? '✓' : '—'}</td>
                  <td>{(c.onboarding_activated_at || c.activated_at) ? '✓' : '—'}</td>
                  <td>{c.insights_consumed_at ? '✓' : '—'}</td>
                  <td>{c.growth_path_id ? '✓' : '—'}</td>
                </>}
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-stone-400">Aucun collaborateur</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
